import { Product } from "@domain/product/product";
import { ProductConfiguration } from "@domain/project/configurations/product-configuration";
import { Regard3000Configuration } from "@domain/project/configurations/regard-3000-configuration";
import { Regard7000PlusConfiguration } from "@domain/project/configurations/regard7000PlusConfiguration";
import { ProjectEventType } from "@domain/project/project-event";
import type { ScopedProject } from "@domain/project/project-guards";
import { ProductItem } from "@project/product-item";
import { Exclude, Expose, plainToInstance, Transform, Type } from "class-transformer";
import { IsArray, IsOptional, IsString, ValidateNested } from "class-validator";
import { v4 as uuidv4 } from "uuid";

export class GasWarningCenterConfiguration extends ProductConfiguration {
  private static readonly NAME_REGEX = /((?:\bRegard\b|\bREGARD\b)\s\d+)/g;

  @IsString()
  public productId: string;

  @Expose()
  @IsArray()
  @ValidateNested({ each: true })
  @Type(() => ProductItem)
  @Transform(({ value, obj }) => {
    // migrate attachmentIds for backward compatibility
    if (Array.isArray(value)) return value;
    if (Array.isArray(obj.attachmentIds)) {
      return obj.attachmentIds.map((id: string) => ProductItem.create(id));
    }
    return [];
  })
  attachments: ProductItem[];

  @Type(() => Regard3000Configuration)
  @IsOptional()
  @Expose({ name: "regard3000Configuration" })
  @Transform(
    ({ obj, value }) => {
      if (value != null) {
        delete obj?.regard3000PlusConfiguration;
        delete obj?.regardPlusConfiguration;
        return value;
      }

      const regard3000PlusConfiguration = obj?.regard3000PlusConfiguration;
      if (regard3000PlusConfiguration != null) {
        delete obj.regard3000PlusConfiguration;
        return plainToInstance(Regard3000Configuration, regard3000PlusConfiguration);
      }

      const regardPlusConfiguration = obj?.regardPlusConfiguration;
      if (regardPlusConfiguration == null) return undefined;

      const regard3000Configuration = plainToInstance(Regard3000Configuration, regardPlusConfiguration);
      delete obj.regardPlusConfiguration;
      return regard3000Configuration;
    },
    { toClassOnly: true },
  )
  private _regard3000Configuration?: Regard3000Configuration;

  @Exclude({ toPlainOnly: true })
  private regard3000PlusConfiguration?: unknown;

  @Exclude({ toPlainOnly: true })
  private regardPlusConfiguration?: unknown;

  @Type(() => Regard7000PlusConfiguration)
  @IsOptional()
  @Expose({ name: "regard7000PlusConfiguration" })
  private _regard7000PlusConfiguration?: Regard7000PlusConfiguration;

  constructor(
    id: string,
    name: string,
    project: ScopedProject,
    productId: string,
    attachments: ProductItem[],
    regard3000Configuration?: Regard3000Configuration,
    regard7000PlusConfiguration?: Regard7000PlusConfiguration,
  ) {
    super(id, name, project);
    this.productId = productId;
    this.attachments = attachments;
    this._regard3000Configuration = regard3000Configuration;
    this._regard7000PlusConfiguration = regard7000PlusConfiguration;
  }

  static create(
    name: string,
    project: ScopedProject,
    productId: string,
    attachments: ProductItem[],
    regard3000Configuration?: Regard3000Configuration,
    regard7000PlusConfiguration?: Regard7000PlusConfiguration,
  ): GasWarningCenterConfiguration {
    return new GasWarningCenterConfiguration(
      uuidv4(),
      name,
      project,
      productId,
      attachments,
      regard3000Configuration,
      regard7000PlusConfiguration,
    );
  }

  countPlacedProducts(project: ScopedProject = this.project): number {
    return project ? project.countPlacedGasWarningCenters(this) : 0;
  }

  update(
    gasWarningCenter: Product,
    attachments: ProductItem[],
    regard3000Configuration?: Regard3000Configuration,
    regard7000PlusConfiguration?: Regard7000PlusConfiguration,
  ) {
    this.name = gasWarningCenter.name;
    this.productId = gasWarningCenter.id;
    this.attachments = attachments;
    this.regard3000Configuration = regard3000Configuration;
    this.regard7000PlusConfiguration = regard7000PlusConfiguration;
    this.project.publishUpdate(ProjectEventType.GAS_WARNING_CENTER_CONFIGURATION_UPDATED, this);
  }

  @Expose({ name: "regard3000Configuration" })
  get regard3000Configuration(): Regard3000Configuration | undefined {
    return this._regard3000Configuration;
  }

  get regard7000PlusConfiguration(): Regard7000PlusConfiguration | undefined {
    return this._regard7000PlusConfiguration;
  }

  set regard3000Configuration(value: Regard3000Configuration | undefined) {
    this._regard3000Configuration = value;
  }

  set regard7000PlusConfiguration(value: Regard7000PlusConfiguration | undefined) {
    this._regard7000PlusConfiguration = value;
  }

  override get mainProductId(): string {
    return this.productId;
  }

  override get configuredProductIds(): string[] {
    if (this.regard3000Configuration) {
      return [
        ...this.regard3000Configuration.baseUnits,
        ...(this.regard3000Configuration.lidUnits ?? []),
        ...(this.regard3000Configuration.inputModules ?? []),
        ...(this.regard3000Configuration.relayModules ?? []),
        ...(this.regard3000Configuration.gatewayModules ?? []),
        ...(this.regard3000Configuration.slotCovers ? [this.regard3000Configuration.slotCovers] : []),
      ]
        .map((productItem) => productItem.productId)
        .concat(this.attachmentIds);
    }
    if (this.regard7000PlusConfiguration) {
      return [
        ...this.regard7000PlusConfiguration.dockingStations,
        ...this.regard7000PlusConfiguration.inputModules,
        ...this.regard7000PlusConfiguration.relayModules,
        ...this.regard7000PlusConfiguration.gatewayModules,
        ...this.regard7000PlusConfiguration.terminalBlocks,
        ...this.regard7000PlusConfiguration.dashboards,
      ]
        .map((pwq) => pwq.productId)
        .concat(this.attachmentIds);
    }
    return this.attachmentIds.concat(this.productId);
  }

  override get productDataName(): string {
    return GasWarningCenterConfiguration.getProductDataName(this.name);
  }

  get attachmentIds(): string[] {
    return this.attachments.map((attachment) => attachment.productId);
  }

  static getProductDataName(name: string): string {
    const regexMatch = name.match(GasWarningCenterConfiguration.NAME_REGEX);
    if (!regexMatch || !regexMatch.length) {
      return "";
    }
    const modifiedName = regexMatch[0].replace(" ", "_").toLowerCase();
    return modifiedName.charAt(0).toUpperCase() + modifiedName.slice(1);
  }
}
